<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Earth zone of interest"
          >
            Earth zones of interest are made of either isolated points of
            interest or Earth meshes.
          </exo-help-dialog>
        </template>
        <v-row>
          <v-col cols="4" class="form-column">
            <exo-input-name :root="_self" />

            <v-row no-gutters>
              <exo-input-ref-multiple
                :root="_self"
                name="isolatedPointsOfInterest"
                :isParametric="false"
                type="isolatedPointsOfInterest"
                componentRoute="/analyticalconstellationearthpointofinterest"
              >
                <exo-help-dialog
                  margin="ml-3 mt-5"
                  title="Isolated points of interest"
                >
                  The isolated points correspond to single points of interest
                  such as ground stations or cities for instance.
                </exo-help-dialog>
              </exo-input-ref-multiple>
            </v-row>
            <v-row no-gutters>
              <exo-input-ref-multiple
                :root="_self"
                name="earthMeshes"
                :isParametric="false"
                type="earthMesh"
                componentRoute="/analyticalconstellationearthmesh"
              >
                <exo-help-dialog margin="ml-3 mt-5" title="Earth mesh">
                  The Earth meshes are rectangular areas on the Earth surface.
                  It is possible to select multiple geographic areas.
                </exo-help-dialog>
              </exo-input-ref-multiple>
            </v-row>
            <v-row
              no-gutters
              v-if="
                (inputs.isolatedPointsOfInterest.length === 0 ||
                  inputs.isolatedPointsOfInterest == null) &&
                (inputs.earthMeshes.length === 0 || inputs.earthMesh == null)
              "
              ><exo-form-alert type="warning"
                >You need to set at least 1 point of interest or 1 earth
                mesh.</exo-form-alert
              ></v-row
            >
          </v-col>
          <v-col cols="8">
            <cesium-viewer
              :poi="inputs.isolatedPointsOfInterest"
              :points="meshesPoints"
              :meshes="meshesList"
            />
          </v-col>
        </v-row>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import formAnalyticalMixin from '../../mixins/analyticalFoms.js'
import constellationComponentsLoader from '../../components/Forms/analyticalConstellationsComponents.js'
import { mapGetters, mapActions } from 'vuex'
import dataTools from '../../tools/data'
import fieldsDescription from '../../tools/fieldsDescription'
import ZOIViewer from './ZoneOfInterestViewer.vue'
import './formsStyle.css'

constellationComponentsLoader()

export default {
  name: 'AnalyticalConstellationEarthZoneOfInterest',

  mixins: [formMixin, formAnalyticalMixin],

  mounted() {
    this.refreshEarthMesh()
    this.refreshIsolatedPointsOfInterest()
  },
  components: {
    'cesium-viewer': ZOIViewer
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'isolatedPointsOfInterestNames',
          text: 'Isolated points of interest (longitude, latitude)',
          sortable: true
        },
        { value: 'earthMeshes', text: 'Earth mesh', sortable: true },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,

      enumPropertiesList: this.getEnumProperties(),

      earthMeshsNotParametrics: [],
      isolatedPointsOfInterestsNotParametrics: [],

      inputs: {
        isolatedPointsOfInterest: [],
        earthMeshes: []
      },
      meshesPoints: [],
      meshesList: []
    }
  },

  methods: {
    ...mapActions(['setCurrentMission']),

    refreshReferences() {
      this.refreshEarthMesh()
      this.refreshIsolatedPointsOfInterest()
    },

    getObjectSubType(instance) {
      return null
    },

    getRequiredPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        this.getObjectSubType(instance),
        'required'
      )
    },

    getOptionalPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        this.getObjectSubType(instance),
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    getCurrentFormTitle() {
      return 'Zone Of Interest'
    },

    getCurrentFormType() {
      return 'ZoneOfInterestDefinition'
    },

    refreshEarthMesh() {
      let self = this
      this.$http
        .get(this.getAPI(`earthmeshdefinitions`))
        .then((response) => {
          self.earthMeshsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of earth meshes. ',
            e,
            'notification',
            'error'
          )
        })
    },

    refreshIsolatedPointsOfInterest() {
      let self = this
      this.$http
        .get(this.getAPI(`pointofinterestdefinitions`))
        .then((response) => {
          self.isolatedPointsOfInterestsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of isolated points of interest. ',
            e,
            'notification',
            'error'
          )
        })
    },

    async computeEarthMesh(meshFormObject) {
      let meshPoints = await this.$http
        .post('/physics/getearthmeshcoordinates', meshFormObject)
        .then(function (response) {
          return response.data
        })
        .catch((e, __) => {
          this.errorTrace = this.getErrorMessage(e)
          return null
        })
      return meshPoints
    },

    getRefList(param) {
      if (param === 'earthMesh') {
        return this.earthMeshsNotParametrics
      }
    },

    getCustomFields() {
      return ['isolatedPointsOfInterestNames', 'earthMeshes']
    },

    displayCustomValue(data, field) {
      switch (field) {
        case 'isolatedPointsOfInterestNames':
          return this.computeIsolatedPointsOfInterestNames(data)
        case 'earthMeshes':
          return this.computeearthMeshes(data)
      }
      return ''
    },

    computeIsolatedPointsOfInterestNames(data) {
      if (data.isolatedPointsOfInterest == null) {
        return ''
      }
      let isolatedPointsOfInterestNames = ''
      for (let i = 0; i < data.isolatedPointsOfInterest.length; i++) {
        isolatedPointsOfInterestNames +=
          data.isolatedPointsOfInterest[i].name +
          ': (' +
          dataTools.formatAngle(
            dataTools.rad2deg(data.isolatedPointsOfInterest[i].longitude)
          ) +
          ', ' +
          dataTools.formatAngle(
            dataTools.rad2deg(data.isolatedPointsOfInterest[i].latitude)
          ) +
          ')'
        isolatedPointsOfInterestNames += ' / '
      }
      return isolatedPointsOfInterestNames.substring(
        0,
        isolatedPointsOfInterestNames.length - 3
      )
    },

    computeearthMeshes(data) {
      if (data.earthMeshes == null) {
        return ''
      }
      let earthMeshes = ''
      for (let i = 0; i < data.earthMeshes.length; i++) {
        earthMeshes += data.earthMeshes[i].name + ' / '
      }
      return earthMeshes.substring(0, earthMeshes.length - 3)
    },

    getCustomInfos(item) {
      let infos = ''
      if (
        item.isolatedPointsOfInterest != null &&
        item.isolatedPointsOfInterest?.length > 0
      ) {
        infos +=
          item.isolatedPointsOfInterest.length == 1
            ? 'Isolated point of interest: '
            : 'Isolated points of interest: '
        infos += this.computeIsolatedPointsOfInterestNames(item) + '\n'
      }
      if (item.earthMeshes != null && item.earthMeshes?.length > 0) {
        infos +=
          item.earthMeshes.length == 1 ? 'Earth mesh: ' : 'Earth meshes: '
        infos += this.computeearthMeshes(item) + '\n'
      }
      return infos
    },
    isExoInputMultiple(item) {
      return item === 'isolatedPointsOfInterest' || item === 'earthMeshs'
    }
  },

  computed: {
    ...mapGetters(['currentMission'])
  },

  watch: {
    'inputs.earthMeshes': {
      async handler() {
        if (!this.paramsValid) {
          return
        }
        let meshesPoints = []
        if (this.inputs.earthMeshes && this.inputs.earthMeshes.length > 0) {
          this.inputs.earthMeshes.forEach(async (meshFormObject) => {
            this.computeEarthMesh(meshFormObject).then((points) => {
              meshesPoints.push(points)
            })
          })
          this.meshesPoints = meshesPoints
          this.meshesList = this.inputs.earthMeshes
        } else {
          this.meshesPoints = []
          this.meshesList = []
        }
      },
      deep: true
    }
  }
}
</script>
